import { useState } from "react";
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
} from "@/common/components/ui";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { DatePicker } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

interface CalculatorData {
  dateOfBirth: string;
  existingCorpus: string;
  monthlyContribution: string;
  retirementAge: string;
  returnOnInvestment: string;
  governmentBond: string;
  annualIncrease: string;
  annuityOption: string;
  lifeExpectancy: string;
}

const PensionCalculator = () => {
  const [calculatorData, setCalculatorData] = useState<CalculatorData>({
    dateOfBirth: "15/08/2007",
    existingCorpus: "100",
    monthlyContribution: "1000",
    retirementAge: "60",
    returnOnInvestment: "10",
    governmentBond: "68",
    annualIncrease: "5",
    annuityOption: "40",
    lifeExpectancy: "6.5",
  });

  const [result, setResult] = useState<{
    totalCorpus: number;
    lumpumWithdrawal: number;
    monthlyPension: number;
  } | null>(null);

  const calculatePension = () => {
    const monthlyAmount = parseInt(calculatorData.monthlyContribution);
    const existingAmount = parseInt(calculatorData.existingCorpus);
    const retirementAge = parseInt(calculatorData.retirementAge);
    const annualReturn = parseFloat(calculatorData.returnOnInvestment) / 100;
    const annualIncrease = parseFloat(calculatorData.annualIncrease) / 100;

    // Calculate age from date of birth (simplified)
    const currentAge = 25; // You can implement proper age calculation from DOB
    const years = retirementAge - currentAge;
    const monthlyReturn = annualReturn / 12;

    // Enhanced calculation with existing corpus and annual increases
    let totalCorpus = existingAmount;
    let currentMonthly = monthlyAmount;

    for (let year = 0; year < years; year++) {
      const monthsInYear = 12;
      for (let month = 0; month < monthsInYear; month++) {
        totalCorpus = totalCorpus * (1 + monthlyReturn) + currentMonthly;
      }
      currentMonthly = currentMonthly * (1 + annualIncrease);
    }

    const lumpumWithdrawal = totalCorpus * 0.6; // 60% lumpsum
    const remainingCorpus = totalCorpus * 0.4; // 40% for annuity
    const lifeExpectancyRate = parseFloat(calculatorData.lifeExpectancy) / 100;
    const monthlyPension = (remainingCorpus * lifeExpectancyRate) / 12;

    setResult({
      totalCorpus: Math.round(totalCorpus),
      lumpumWithdrawal: Math.round(lumpumWithdrawal),
      monthlyPension: Math.round(monthlyPension),
    });
  };

  const resetCalculator = () => {
    setCalculatorData({
      dateOfBirth: "15/08/2007",
      existingCorpus: "100",
      monthlyContribution: "1000",
      retirementAge: "60",
      returnOnInvestment: "10",
      governmentBond: "68",
      annualIncrease: "5",
      annuityOption: "40",
      lifeExpectancy: "6.5",
    });
    setResult(null);
  };

  const chartData = result
    ? [
        {
          name: "Lumpsum Withdrawal",
          value: result.lumpumWithdrawal,
          color: "#F59E0B",
        },
        {
          name: "Monthly Pension",
          value: result.monthlyPension * 240,
          color: "#10B981",
        }, // Approximate 20 years
        {
          name: "Remaining",
          value:
            result.totalCorpus -
            result.lumpumWithdrawal -
            result.monthlyPension * 240,
          color: "#3B82F6",
        },
      ]
    : [
        { name: "Lumpsum Withdrawal", value: 5577447, color: "#F59E0B" },
        { name: "Monthly Pension", value: 5020600, color: "#10B981" },
        { name: "Remaining", value: 2389495, color: "#3B82F6" },
      ];

  const displayCorpus = result ? result.totalCorpus : 13987542;

  return (
    <>
      <style>
        {`
        .react-datepicker-wrapper {
          width: 100%;
        }
      `}
      </style>

      <section className="mx-auto my-4 w-full bg-white px-4 md:my-8 lg:my-16 lg:px-10">
        <div className="container mx-auto">
          <div className="mx-auto grid gap-8 lg:grid-cols-2 lg:gap-12">
            {/* Calculator Form */}
            <div className="rounded-xl bg-background p-4 text-white shadow-lg lg:p-8">
              <div className="mb-5 text-center font-sans text-2xl font-bold leading-tight text-[#1D2C56] sm:text-3xl md:mb-8 lg:mb-12 lg:text-5xl lg:leading-[60px]">
                Pension Calculator NPS
              </div>
              <form
                className="space-y-3 lg:space-y-6"
                onSubmit={e => {
                  e.preventDefault();
                  calculatePension();
                }}
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-6">
                  <div>
                    <Label className="mb-2 block font-sans text-[15px] font-bold text-[#707070]">
                      Date of Birth
                    </Label>
                    <DatePicker
                      placeholder="dd/mm/yyyy"
                      value={calculatorData.dateOfBirth}
                      onChange={(date: Date | null) =>
                        setCalculatorData({
                          ...calculatorData,
                          dateOfBirth: date?.toLocaleDateString() ?? "",
                        })
                      }
                      className="inline-flex w-full items-center justify-start rounded-md bg-white px-2 py-2 font-sans font-bold text-[#2F363F] outline outline-1 outline-offset-[-1px] outline-[#DFE7EC]"
                      data-testid="input-date-of-birth"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block font-sans text-[15px] font-bold text-[#707070]">
                      Existing NPS Tier 1 Corpus
                    </Label>
                    <Input
                      type="number"
                      placeholder="₹100"
                      value={calculatorData.existingCorpus}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCalculatorData({
                          ...calculatorData,
                          existingCorpus: e.target.value,
                        })
                      }
                      className="inline-flex w-full items-center justify-start rounded-md bg-white px-2 py-2 font-sans font-bold text-[#2F363F] outline outline-1 outline-offset-[-1px] outline-[#DFE7EC]"
                      data-testid="input-existing-corpus"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-6">
                  <div>
                    <Label className="mb-2 block font-sans text-[15px] font-bold text-[#707070]">
                      I would like to contribute (per month)
                    </Label>
                    <Input
                      type="number"
                      placeholder="₹1000"
                      value={calculatorData.monthlyContribution}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCalculatorData({
                          ...calculatorData,
                          monthlyContribution: e.target.value,
                        })
                      }
                      className="bg-white font-sans font-bold text-[#2F363F]"
                      data-testid="input-monthly-contribution"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block font-sans text-[15px] font-bold text-[#707070]">
                      Retirement Age (In Years)
                    </Label>
                    <Select
                      value={calculatorData.retirementAge}
                      onValueChange={value =>
                        setCalculatorData({
                          ...calculatorData,
                          retirementAge: value,
                        })
                      }
                    >
                      <SelectTrigger
                        className="font-sans font-bold text-[#2F363F]"
                        data-testid="select-retirement-age"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="58">58</SelectItem>
                        <SelectItem value="60">60</SelectItem>
                        <SelectItem value="62">62</SelectItem>
                        <SelectItem value="65">65</SelectItem>
                        <SelectItem value="70">70</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-6">
                  <div className="w-full">
                    <Label className="mb-2 block font-sans text-[15px] font-bold text-[#707070]">
                      My Expected return on Investment is (%)
                    </Label>
                    <Select
                      value={calculatorData.returnOnInvestment}
                      onValueChange={value =>
                        setCalculatorData({
                          ...calculatorData,
                          returnOnInvestment: value,
                        })
                      }
                    >
                      <SelectTrigger
                        className="font-sans font-bold text-[#2F363F]"
                        data-testid="select-return-investment"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="8">8%</SelectItem>
                        <SelectItem value="10">10%</SelectItem>
                        <SelectItem value="12">12%</SelectItem>
                        <SelectItem value="15">15%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-6">
                  <div>
                    <Label className="mb-2 block font-sans text-[15px] font-bold text-[#707070]">
                      Defer withdrawal until age (Years)
                    </Label>
                    <Select
                      value={calculatorData.annualIncrease}
                      onValueChange={value =>
                        setCalculatorData({
                          ...calculatorData,
                          annualIncrease: value,
                        })
                      }
                    >
                      <SelectTrigger
                        className="font-sans font-bold text-[#2F363F]"
                        data-testid="select-annual-increase"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3%</SelectItem>
                        <SelectItem value="5">5%</SelectItem>
                        <SelectItem value="7">7%</SelectItem>
                        <SelectItem value="10">10%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-2 block font-sans text-[15px] font-bold text-[#707070]">
                      % Annual Increase in contribution
                    </Label>
                    <Select
                      value={calculatorData.lifeExpectancy}
                      onValueChange={value =>
                        setCalculatorData({
                          ...calculatorData,
                          lifeExpectancy: value,
                        })
                      }
                    >
                      <SelectTrigger
                        className="font-sans font-bold text-[#2F363F]"
                        data-testid="select-life-expectancy"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5.5">5.5%</SelectItem>
                        <SelectItem value="6">6%</SelectItem>
                        <SelectItem value="6.5">6.5%</SelectItem>
                        <SelectItem value="7">7%</SelectItem>
                        <SelectItem value="7.5">7.5%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:gap-6">
                  <div>
                    <Label className="mb-2 block font-sans text-[15px] font-bold text-[#707070]">
                      I would like to purchase annuity ratio (%)
                    </Label>
                    <Select
                      value={calculatorData.annuityOption}
                      onValueChange={value =>
                        setCalculatorData({
                          ...calculatorData,
                          annuityOption: value,
                        })
                      }
                    >
                      <SelectTrigger
                        className="font-sans font-bold text-[#2F363F]"
                        data-testid="select-annuity-option"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30</SelectItem>
                        <SelectItem value="35">35</SelectItem>
                        <SelectItem value="40">40</SelectItem>
                        <SelectItem value="45">45</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-2 block font-sans text-[15px] font-bold text-[#707070]">
                      I am expecting an annuity rate of (%)
                    </Label>
                    <Select
                      value={calculatorData.lifeExpectancy}
                      onValueChange={value =>
                        setCalculatorData({
                          ...calculatorData,
                          lifeExpectancy: value,
                        })
                      }
                    >
                      <SelectTrigger
                        className="font-sans font-bold text-[#2F363F]"
                        data-testid="select-life-expectancy-2"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5.5">5.5%</SelectItem>
                        <SelectItem value="6">6%</SelectItem>
                        <SelectItem value="6.5">6.5%</SelectItem>
                        <SelectItem value="7">7%</SelectItem>
                        <SelectItem value="7.5">7.5%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex flex-col flex-wrap gap-3 pt-2 sm:flex-row sm:gap-4 md:pt-4">
                  <Button
                    variant="outline"
                    className="rounded-[8px] bg-[#DFE7EC] text-center font-['Nunito'] font-sans text-[18px] font-bold leading-8 text-[#2F363F] md:justify-start"
                    data-testid="button-reset"
                    onClick={resetCalculator}
                  >
                    Reset
                  </Button>
                </div>
              </form>
            </div>

            {/* Results Section */}
            <div className="h-full w-full space-y-6 rounded-[30px] bg-[#E4FFFB]">
              {/* Top Results Card */}
              <div className="inline-flex w-full flex-col items-center justify-start gap-4 rounded-tl-[30px] rounded-tr-[30px] bg-gradient-to-r from-[#1D2C56] to-[#66A0CD] p-4 font-sans text-white shadow-[0px_8px_20px_0px_rgba(0,0,0,0.10)] md:p-7">
                <h3 className="mb-3 font-sans text-[20px] font-semibold md:text-[25px]">
                  Your Projected Corpus at the time of exit:{" "}
                  {result
                    ? `${(result.totalCorpus / 10000000).toFixed(2)} Cr`
                    : "0.93 Cr"}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2">
                  <div className="border-none md:border-r-[3px] md:border-[#FFFFFF33] md:pr-[35px]">
                    <h4 className="mb-1 font-sans text-[18px] font-semibold text-[#fff]">
                      Lumpsum Withdrawal
                    </h4>
                    <p className="text-center font-sans text-[22px] font-semibold text-[#fff]">
                      ₹{" "}
                      {result
                        ? result.lumpumWithdrawal.toLocaleString("en-IN")
                        : "55,77,447"}
                    </p>
                  </div>
                  <div>
                    <h4 className="mb-1 text-center font-sans text-[18px] font-semibold text-[#fff]">
                      Monthly Pension
                    </h4>
                    <p className="text-center text-[22px] text-xl font-semibold text-[#fff]">
                      ₹{" "}
                      {result
                        ? result.monthlyPension.toLocaleString("en-IN")
                        : "20,915"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Chart Section */}
              <div className="rounded-xl p-4 md:p-6">
                <h3 className="mb-6 text-center font-sans text-lg font-bold text-[#2F363F]">
                  Status of your Pension Account at retirement
                </h3>

                <div className="relative mx-auto mb-6 h-64 w-64 sm:h-80 sm:w-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={140}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {chartData.map(entry => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>

                  {/* Center Text */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <p className="mb-1 font-sans text-lg font-semibold text-[#707070]">
                      Total Corpus
                    </p>
                    <p className="font-sans text-2xl font-bold text-[#2F363F]">
                      ₹ {displayCorpus.toLocaleString("en-IN")}
                    </p>
                  </div>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap justify-center gap-4 text-sm sm:gap-6">
                  <div className="flex items-center">
                    <div className="mr-2 h-4 w-4 rounded bg-yellow-500" />
                    <span className="font-sans text-sm font-bold text-[#2F363F]">
                      Lumpsum Withdrawal
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className="mr-2 h-4 w-4 rounded bg-green-500" />
                    <span className="font-sans text-sm font-bold text-[#2F363F]">
                      Monthly Pension
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className="mr-2 h-4 w-4 rounded bg-blue-500" />
                    <span className="font-sans text-sm font-bold text-[#2F363F]">
                      Remaining
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PensionCalculator;
